import { z } from 'zod';

import {
  COST_CENTERS,
  CURRENCIES,
  EMPLOYMENT_TYPES,
  FIELD_SETS,
  RECRUITMENT_LANGUAGES,
  TICKET_PRIORITIES,
  TICKET_STATUSES,
} from '../domain/enums.js';
import {
  isoDateSchema,
  isoDateTimeSchema,
  paginationQuerySchema,
  richTextSchema,
  shortTextSchema,
  sortDirectionSchema,
  uuidSchema,
} from './common.js';

export const fieldSetSchema = z.enum(FIELD_SETS);
export const ticketStatusSchema = z.enum(TICKET_STATUSES);
export const ticketPrioritySchema = z.enum(TICKET_PRIORITIES);
export const costCenterSchema = z.enum(COST_CENTERS);
export const currencySchema = z.enum(CURRENCIES);
export const recruitmentLanguageSchema = z.enum(RECRUITMENT_LANGUAGES);
export const employmentTypeSchema = z.enum(EMPLOYMENT_TYPES);

const baseCreateFields = {
  boardId: uuidSchema,
  title: shortTextSchema,
  description: richTextSchema.nullable().default(null),
  priority: ticketPrioritySchema.default('normal'),
  emailAssignee: z.boolean().default(true),
  assigneeId: uuidSchema.nullable().default(null),
};

export const createBasicTicketSchema = z.object({
  ...baseCreateFields,
  fieldSet: z.literal('basic'),
  departmentId: uuidSchema.nullable().default(null),
  projectId: uuidSchema.nullable().default(null),
  neededBy: isoDateSchema.nullable().default(null),
});

const purchaseShape = z.object({
  ...baseCreateFields,
  fieldSet: z.literal('purchase'),
  costCenter: costCenterSchema,
  projectId: uuidSchema.nullable().default(null),
  estimatedAmount: z.number().int().min(0).max(99_999_999).nullable().default(null),
  currency: currencySchema.default('EUR'),
  neededBy: isoDateSchema.nullable().default(null),
  deliveryLocation: shortTextSchema.nullable().default(null),
  projectNote: shortTextSchema.nullable().default(null),
});

const CLIENT_PURCHASE_NEEDS_PROJECT = {
  message: 'A client-billable purchase must name the project it is billed to.',
  path: ['projectId'] as const,
};

export const createPurchaseSchema = purchaseShape.refine(
  (value) => value.costCenter !== 'client' || value.projectId !== null,
  { message: CLIENT_PURCHASE_NEEDS_PROJECT.message, path: [...CLIENT_PURCHASE_NEEDS_PROJECT.path] },
);

export const createRecruitmentSchema = z.object({
  ...baseCreateFields,
  fieldSet: z.literal('recruitment'),
  position: shortTextSchema,
  language: recruitmentLanguageSchema,
  deadline: isoDateTimeSchema,
  projectId: uuidSchema,
  employmentType: employmentTypeSchema.nullable().default(null),
  numberOfPeople: z.number().int().min(1).max(999).default(1),
});

export const createTicketSchema = z
  .discriminatedUnion('fieldSet', [
    createBasicTicketSchema,
    purchaseShape,
    createRecruitmentSchema,
  ])
  .superRefine((value, ctx) => {
    if (
      value.fieldSet === 'purchase' &&
      value.costCenter === 'client' &&
      value.projectId === null
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: CLIENT_PURCHASE_NEEDS_PROJECT.message,
        path: [...CLIENT_PURCHASE_NEEDS_PROJECT.path],
      });
    }
  });

export type CreateBasicTicketInput = z.infer<typeof createBasicTicketSchema>;
export type CreatePurchaseInput = z.infer<typeof createPurchaseSchema>;
export type CreateRecruitmentInput = z.infer<typeof createRecruitmentSchema>;
export type CreateTicketInput = z.infer<typeof createTicketSchema>;

export const updateTicketSchema = z
  .object({
    title: shortTextSchema.optional(),
    description: richTextSchema.nullable().optional(),
    priority: ticketPrioritySchema.optional(),
    departmentId: uuidSchema.optional(),
    assigneeId: uuidSchema.nullable().optional(),
    projectId: uuidSchema.nullable().optional(),
    estimatedAmount: z.number().int().min(0).max(99_999_999).nullable().optional(),
    currency: currencySchema.optional(),
    neededBy: isoDateSchema.nullable().optional(),
    costCenter: costCenterSchema.optional(),
    deliveryLocation: shortTextSchema.nullable().optional(),
    projectNote: shortTextSchema.nullable().optional(),
    position: shortTextSchema.optional(),
    language: recruitmentLanguageSchema.optional(),
    deadline: isoDateTimeSchema.optional(),
    employmentType: employmentTypeSchema.nullable().optional(),
    numberOfPeople: z.number().int().min(1).max(999).optional(),
  })
  .strict()
  .refine((value) => Object.keys(value).length > 0, 'Nothing to update.');

export type UpdateTicketInput = z.infer<typeof updateTicketSchema>;

export const TICKET_SORT_FIELDS = ['createdAt', 'updatedAt', 'priority', 'number'] as const;

export const ticketListQuerySchema = paginationQuerySchema.extend({
  boardId: uuidSchema.optional(),
  status: ticketStatusSchema.optional(),
  priority: ticketPrioritySchema.optional(),
  stageId: uuidSchema.optional(),
  departmentId: uuidSchema.optional(),
  assigneeId: uuidSchema.optional(),
  requesterId: uuidSchema.optional(),
  projectId: uuidSchema.optional(),
  search: z.string().trim().min(1).max(200).optional(),
  includeClosed: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .default(true)
    .transform((value) => value === true || value === 'true'),
  archived: z.enum(['exclude', 'only', 'all']).default('exclude'),
  sortBy: z.enum(TICKET_SORT_FIELDS).default('createdAt'),
  sortDirection: sortDirectionSchema,
});

export type TicketListQuery = z.infer<typeof ticketListQuerySchema>;

export const addCommentSchema = z.object({
  body: richTextSchema.min(1),
  internal: z.boolean().default(false),
});

export type AddCommentInput = z.infer<typeof addCommentSchema>;
